import React, { Suspense, lazy, useEffect } from 'react';
import { useDraftDock } from '../../context/DraftDockContext';

/**
 * The always-mounted half of the scratchpad.
 *
 * Two jobs, both of which the dock itself can't do:
 *
 * 1. Listen for `open-draft`. The dock is unmounted when closed, so something
 *    that is always present has to hear the shortcut that opens it. This reuses
 *    the window-event bridge the project Layout already uses for `open-new-task`
 *    and friends, rather than inventing a second mechanism.
 *
 *    The shortcut always asks for a *blank* draft. `requestNew` mints a fresh id
 *    for it, so this one line covers every starting state: closed, collapsed, or
 *    open on some other note.
 *
 *    `toggle-draft-split` (⌘⇧J) rides the same bridge, gated on `inlineWanted`:
 *    only a route that has offered the dock a pane of its own has room for two
 *    drafts side by side, so off the console the chord does nothing rather than
 *    quietly rearranging a panel you cannot see.
 *
 * 2. Keep the editor out of the initial bundle. react-markdown, remark-gfm and
 *    the block editor are a meaningful chunk, and most page loads never open a
 *    draft — so they load on the first `q` instead of on every visit.
 */

const DraftDock = lazy(() => import('./DraftDock'));

export default function DraftDockHost() {
    const dock = useDraftDock();
    const requestNew = dock?.requestNew;
    const toggleSplit = dock?.toggleSplit;
    const enabled = dock?.enabled;
    const inlineWanted = dock?.inlineWanted;

    useEffect(() => {
        if (!enabled || !requestNew) return undefined;
        const onOpen = () => requestNew();
        window.addEventListener('open-draft', onOpen);
        return () => window.removeEventListener('open-draft', onOpen);
    }, [enabled, requestNew]);

    useEffect(() => {
        if (!enabled || !inlineWanted || !toggleSplit) return undefined;
        const onSplit = () => toggleSplit();
        window.addEventListener('toggle-draft-split', onSplit);
        return () => window.removeEventListener('toggle-draft-split', onSplit);
    }, [enabled, inlineWanted, toggleSplit]);

    // `fallback={null}` on purpose: the chunk resolves in well under a frame on
    // a warm cache, and a spinner that flashes in the corner of the screen is
    // worse than the panel simply appearing.
    if (!enabled || !dock.isOpen) return null;
    return (
        <Suspense fallback={null}>
            <DraftDock />
        </Suspense>
    );
}
